import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AppNav from "../components/appNav";
import Footer from "../components/footer";
import PageSection from "../components/PageSection";
import ArrowIcon from "../components/ArrowIcon";
import { submitContactBrief } from "../firebase";

type Step = [string, string];

type Status = "idle" | "sending" | "success" | "error";

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const steps = t("kode.contact.steps", { returnObjects: true }) as unknown as Step[];
  const [status, setStatus] = useState<Status>("idle");
  const [form, setForm] = useState({
    fullName: "",
    organisation: "",
    role: "",
    productOfInterest: "",
    problem: "",
    email: "",
    phone: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("sending");
    const result = await submitContactBrief({
      fullName: form.fullName.trim(),
      organisation: form.organisation.trim(),
      role: form.role.trim(),
      productOfInterest: form.productOfInterest.trim() || null,
      problem: form.problem.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      lang: (i18n.language || "en").startsWith("es") ? "es" : "en",
    });
    if (result.success) {
      setStatus("success");
      setForm({
        fullName: "",
        organisation: "",
        role: "",
        productOfInterest: "",
        problem: "",
        email: "",
        phone: "",
      });
    } else {
      setStatus("error");
    }
  };

  const statusMsg =
    status === "sending"
      ? t("kode.contact.sending")
      : status === "success"
      ? t("kode.contact.success")
      : status === "error"
      ? t("kode.contact.error")
      : "";

  const productOptions = [
    "BOGOWI",
    "B:web3 Wallet",
    "EasyChain",
    "b:did",
    "Islas Vivas",
    t("kode.products.productivity.name"),
    t("kode.products.documents.name"),
  ];

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-50 bg-paper/95 backdrop-blur-[2px] border-b border-ink-14">
        <AppNav />
      </header>

      <main>
        {/* 01 / Hero */}
        <PageSection marker={t("kode.contact.sectionLabel")} noTop topPadClass="!pt-12">
          <h1 className="section-h-lg">{t("kode.contact.h1")}</h1>
          <p className="text-body-lg text-ink-70 mt-6" style={{ maxWidth: "60ch" }}>
            {t("kode.contact.lead")}
          </p>
        </PageSection>

        {/* 02 / How it works */}
        <PageSection marker={t("kode.contact.howLabel")}>
          <h2 className="section-h">{t("kode.contact.howH2")}</h2>
          <div className="props" style={{ marginTop: "2rem" }}>
            {steps.map(([k, v], i) => (
              <div className="row" key={i}>
                <div className="k">{k}</div>
                <div className="v">{v}</div>
              </div>
            ))}
          </div>
        </PageSection>

        {/* 03 / Brief */}
        <PageSection marker={t("kode.contact.briefLabel")}>
          <h2 className="section-h">{t("kode.contact.briefH2")}</h2>
          <p className="section-sub">{t("kode.contact.briefSub")}</p>

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6 mt-7"
            noValidate
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="fullName" className="swiss-caption text-ink">{t("kode.contact.formName")}</label>
              <input
                id="fullName" name="fullName" type="text" required
                value={form.fullName} onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="organisation" className="swiss-caption text-ink">{t("kode.contact.formOrg")}</label>
              <input
                id="organisation" name="organisation" type="text" required
                value={form.organisation} onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="role" className="swiss-caption text-ink">{t("kode.contact.formRole")}</label>
              <input
                id="role" name="role" type="text"
                placeholder={t("kode.contact.formRolePlaceholder")}
                value={form.role} onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="productOfInterest" className="swiss-caption text-ink">{t("kode.contact.formProduct")}</label>
              <select
                id="productOfInterest" name="productOfInterest"
                value={form.productOfInterest} onChange={handleChange}
                className="form-input"
              >
                <option value="">{t("kode.contact.formProductAny")}</option>
                {productOptions.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 flex flex-col gap-2">
              <label htmlFor="problem" className="swiss-caption text-ink">{t("kode.contact.formProblem")}</label>
              <textarea
                id="problem" name="problem" rows={5}
                placeholder={t("kode.contact.formProblemPlaceholder")}
                value={form.problem} onChange={handleChange}
                className="form-input"
                style={{ resize: "vertical", minHeight: 120 }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="swiss-caption text-ink">{t("kode.contact.formEmail")}</label>
              <input
                id="email" name="email" type="email" required
                value={form.email} onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="phone" className="swiss-caption text-ink">{t("kode.contact.formPhone")}</label>
              <input
                id="phone" name="phone" type="tel"
                value={form.phone} onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="md:col-span-2 mt-3 flex flex-wrap items-center gap-3.5">
              <button type="submit" className="btn btn-primary" disabled={status === "sending"}>
                {t("kode.contact.submit")}
                <ArrowIcon className="h-4 w-4" />
              </button>
              <span className="swiss-caption text-ink-50">{t("kode.contact.submitNote")}</span>
            </div>
            {statusMsg && (
              <div
                className={`brief-status ${status}`}
                role="status"
                aria-live="polite"
              >
                {statusMsg}
              </div>
            )}
          </form>
        </PageSection>

        {/* 04 / Direct lines */}
        <PageSection marker={t("kode.contact.linesLabel")}>
          <h2 className="section-h">{t("kode.contact.linesH2")}</h2>
          <div className="lines">
            <div className="row">
              <div className="k">{t("kode.contact.lineEmail")}</div>
              <div className="v"><a href="mailto:hugo@kode.zone">hugo@kode.zone</a></div>
            </div>
            <div className="row">
              <div className="k">{t("kode.contact.linePhone")}</div>
              <div className="v"><a href="tel:+34636349614">+34 636 349 614</a></div>
            </div>
            <div className="row">
              <div className="k">{t("kode.contact.lineOffice")}</div>
              <div className="v">{t("kode.contact.officeAddress")}</div>
            </div>
            <div className="row">
              <div className="k">{t("kode.contact.lineHours")}</div>
              <div className="v">{t("kode.contact.officeHours")}</div>
            </div>
          </div>
        </PageSection>

        <Footer />
      </main>
    </div>
  );
};

export default ContactPage;
